import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '../../utils/platform';
import { getCashBook, createCashTransfer, deleteCashTransfer, getCashTransfers, createCurrencyExchange, getCurrencyExchanges, deleteCurrencyExchange } from '../../services/api';
import { FiBook, FiRepeat, FiX, FiTrash2, FiCamera, FiPaperclip, FiFile, FiPrinter, FiDollarSign } from 'react-icons/fi';
import printHtml from '../../utils/printHtml';
import useAppRefresh from '../../hooks/useAppRefresh';
import AuthImg from '../../components/AuthImg';

// Bucket display config — fixed order matches the backend.
const BUCKETS = [
  { key: 'usd_cash', currency: '$', label: 'USD Cash',     group: 'usd', color: '#16a34a' },
  { key: 'usd_bank', currency: '$', label: 'USD Bank',     group: 'usd', color: '#16a34a' },
  { key: 'k_cash',   currency: 'K', label: 'K Cash',       group: 'k',   color: '#2563eb' },
  { key: 'k_bank',   currency: 'K', label: 'K Bank',       group: 'k',   color: '#2563eb' },
  { key: 'k_momo',   currency: 'K', label: 'K Mobile',     group: 'k',   color: '#2563eb' },
];

const fmt = (n) => parseFloat(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2 });

const zeroBuckets = () => ({ usd_cash: 0, usd_bank: 0, k_cash: 0, k_bank: 0, k_momo: 0 });

const CashBook = () => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [data, setData] = useState({
    entries: [],
    opening: zeroBuckets(),
    creditTotals: zeroBuckets(),
    debitTotals: zeroBuckets(),
    closing: zeroBuckets(),
    openingDate: null,
  });
  const [from, setFrom] = useState(new Date().toISOString().slice(0, 10));
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));

  // v1.9.69 — Bucket filter. When set, ledger only shows entries that
  // touched this bucket (any non-zero amount on the chosen column).
  // Client-side; keeps the CR / PV / Transfer numbers in the summary
  // cards unchanged.
  const [bucketFilter, setBucketFilter] = useState('');

  // v1.9.61 — Transfer modal state (+ v1.9.62 attachment file & preview)
  const [showTransfer, setShowTransfer] = useState(false);
  const [trfForm, setTrfForm] = useState({ date: new Date().toISOString().slice(0, 10), from_bucket: 'k_cash', to_bucket: 'k_bank', amount: '', description: '' });
  const [trfBusy, setTrfBusy] = useState(false);
  const [trfError, setTrfError] = useState('');
  const [trfFile, setTrfFile] = useState(null);          // the actual File object
  const [trfFilePreview, setTrfFilePreview] = useState(null); // object URL for preview
  const [viewAttachment, setViewAttachment] = useState(null); // lightbox for existing attachments
  const [tab, setTab] = useState('ledger'); // 'ledger' | 'transfers' | 'exchanges'
  const [transfers, setTransfers] = useState([]);
  const [exchanges, setExchanges] = useState([]);
  // v1.9.105 — Currency Exchange modal (USD <-> K at an admin-entered rate).
  // v1.9.106 — any two of {amount_from, amount_to, rate} are enough; the third
  // field is derived + disabled. excDerived names the derived field.
  const [showExchange, setShowExchange] = useState(false);
  const [excForm, setExcForm] = useState({ date: new Date().toISOString().slice(0, 10), from_bucket: 'usd_cash', to_bucket: 'k_cash', amount_from: '', amount_to: '', rate: '', description: '' });
  const [excDerived, setExcDerived] = useState('amount_to'); // 'amount_from' | 'amount_to' | 'rate'
  // Last field the user typed in — it stays editable when a locked field is unlocked.
  const excLastEdited = useRef('amount_from');
  const [excBusy, setExcBusy] = useState(false);
  const [excError, setExcError] = useState('');
  const [excFile, setExcFile] = useState(null);                // slip / receipt File
  const [excFilePreview, setExcFilePreview] = useState(null);  // object URL for preview

  const setTrfFileFromInput = (f) => {
    setTrfFile(f);
    if (trfFilePreview) URL.revokeObjectURL(trfFilePreview);
    setTrfFilePreview(f && f.type.startsWith('image/') ? URL.createObjectURL(f) : null);
  };
  const clearTrfFile = () => setTrfFileFromInput(null);
  const setExcFileFromInput = (f) => {
    setExcFile(f);
    if (excFilePreview) URL.revokeObjectURL(excFilePreview);
    setExcFilePreview(f && f.type.startsWith('image/') ? URL.createObjectURL(f) : null);
  };
  const clearExcFile = () => setExcFileFromInput(null);

  const load = React.useCallback(() => {
    getCashBook({ from, to })
      .then(r => setData(prev => ({ ...prev, ...(r.data || {}) })))
      .catch(() => {});
  }, [from, to]);
  useEffect(() => { load(); }, [load]);
  useAppRefresh(load);

  // v1.9.67 — Transfer history tab: fetch when opened OR when the date
  // filter changes while it's the active tab.
  const loadTransfers = React.useCallback(() => {
    getCashTransfers({ from: from || undefined, to: to || undefined })
      .then(r => setTransfers(Array.isArray(r.data) ? r.data : []))
      .catch(() => setTransfers([]));
  }, [from, to]);
  useEffect(() => { if (tab === 'transfers') loadTransfers(); }, [tab, loadTransfers]);

  // v1.9.119 — Exchange history tab, same pattern as transfers.
  const loadExchanges = React.useCallback(() => {
    getCurrencyExchanges({ from: from || undefined, to: to || undefined })
      .then(r => setExchanges(Array.isArray(r.data) ? r.data : []))
      .catch(() => setExchanges([]));
  }, [from, to]);
  useEffect(() => { if (tab === 'exchanges') loadExchanges(); }, [tab, loadExchanges]);

  const removeExchange = async (id, ref) => {
    if (!window.confirm(`Delete exchange ${ref}?`)) return;
    try { await deleteCurrencyExchange(id); load(); if (tab === 'exchanges') loadExchanges(); }
    catch (e) { alert(e.response?.data?.error || 'Failed to delete exchange'); }
  };

  // Running per-bucket balance threaded down the entries list.
  const running = { ...data.opening };

  // Group totals (sum across each currency's buckets).
  const sumGroup = (b, group) => BUCKETS.filter(x => x.group === group).reduce((s, x) => s + parseFloat(b[x.key] || 0), 0);
  // v1.9.107 — scope a bucket dict to the selected filter so the summary
  // tiles (Opening / Closing / Income / Expense / Balance) only reflect the
  // picked bucket. Pass dict through unchanged when no filter is active.
  const scoped = React.useCallback((dict) => {
    if (!bucketFilter) return dict;
    const z = zeroBuckets();
    z[bucketFilter] = parseFloat(dict?.[bucketFilter] || 0);
    return z;
  }, [bucketFilter]);

  const bucketLabel = (k) => (BUCKETS.find(b => b.key === k) || {}).label || k;
  const bucketCurrency = (k) => (BUCKETS.find(b => b.key === k) || {}).currency || '';

  const submitTransfer = async () => {
    setTrfError('');
    const amt = parseFloat(trfForm.amount);
    if (trfForm.from_bucket === trfForm.to_bucket) { setTrfError('From and To must be different buckets'); return; }
    if (bucketCurrency(trfForm.from_bucket) !== bucketCurrency(trfForm.to_bucket)) {
      setTrfError('Cannot transfer between USD and Kwacha here — use currency exchange');
      return;
    }
    if (!(amt > 0)) { setTrfError('Enter an amount greater than zero'); return; }
    setTrfBusy(true);
    try {
      await createCashTransfer({ ...trfForm, amount: amt, attachment: trfFile || undefined });
      setShowTransfer(false);
      setTrfForm(f => ({ ...f, amount: '', description: '' }));
      clearTrfFile();
      load();
      if (tab === 'transfers') loadTransfers();
    } catch (e) {
      setTrfError(e.response?.data?.error || 'Failed to save transfer');
    } finally { setTrfBusy(false); }
  };

  const removeTransfer = async (id, ref) => {
    if (!window.confirm(`Delete transfer ${ref}?`)) return;
    try { await deleteCashTransfer(id); load(); if (tab === 'transfers') loadTransfers(); }
    catch (e) { alert(e.response?.data?.error || 'Failed to delete transfer'); }
  };

  // v1.9.106 — compute the derived field live from the two user-entered ones.
  // Returns effective { from, to, rate } for submission + preview.
  const computeExchangeTriple = () => {
    const from = parseFloat(excForm.amount_from) || 0;
    const to   = parseFloat(excForm.amount_to)   || 0;
    const rate = parseFloat(excForm.rate)        || 0;
    const fromIsUsd = excForm.from_bucket.startsWith('usd');
    // From-currency * rate = To-currency when USD→K; From-currency / rate = To when K→USD.
    // Equivalently: fromAmount * (fromIsUsd ? rate : 1/rate) = toAmount
    let eFrom = from, eTo = to, eRate = rate;
    if (excDerived === 'amount_to') {
      eTo = fromIsUsd ? from * rate : (rate > 0 ? from / rate : 0);
    } else if (excDerived === 'amount_from') {
      eFrom = fromIsUsd ? (rate > 0 ? to / rate : 0) : to * rate;
    } else { // derived === 'rate'
      // rate = (USD→K) ? to/from : from/to
      eRate = fromIsUsd ? (from > 0 ? to / from : 0) : (to > 0 ? from / to : 0);
    }
    return { from: eFrom, to: eTo, rate: eRate };
  };

  // v1.9.105 — Currency Exchange submit. Backend validates buckets are
  // different currencies and recomputes amount_to from amount_from + rate.
  const submitExchange = async () => {
    setExcError('');
    const t = computeExchangeTriple();
    if (!(t.from > 0)) { setExcError('Amount must be greater than zero'); return; }
    if (!(t.rate > 0)) { setExcError('Rate must be greater than zero'); return; }
    if (excForm.from_bucket === excForm.to_bucket) { setExcError('From and To must be different'); return; }
    setExcBusy(true);
    try {
      await createCurrencyExchange({
        date: excForm.date,
        from_bucket: excForm.from_bucket,
        to_bucket:   excForm.to_bucket,
        amount_from: t.from,
        rate:        t.rate,
        description: excForm.description,
        attachment:  excFile || undefined,
      });
      setShowExchange(false);
      clearExcFile();
      setExcForm({ date: new Date().toISOString().slice(0, 10), from_bucket: 'usd_cash', to_bucket: 'k_cash', amount_from: '', amount_to: '', rate: '', description: '' });
      setExcDerived('amount_to');
      excLastEdited.current = 'amount_from';
      load();
      if (tab === 'exchanges') loadExchanges();
    } catch (e) {
      setExcError(e.response?.data?.error || 'Failed to save exchange');
    } finally { setExcBusy(false); }
  };

  // v1.9.88 — build a self-contained HTML statement for the Print button.
  // Mirrors what's on screen (opening -> entries (filtered + sorted) ->
  // closing) in a clean, printable layout with no sidebar / nav chrome.
  const buildStatementHtml = () => {
    const esc = (v) => String(v ?? '').replace(/[&<>"']/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c]));
    const filtered = bucketFilter
      ? data.entries.filter(e => parseFloat(e[bucketFilter] || 0) !== 0)
      : data.entries;
    const rows = filtered.slice().sort((a, b) =>
      (a.date || '').localeCompare(b.date || '') || String(a.ref || '').localeCompare(String(b.ref || ''))
    );
    const activeBuckets = bucketFilter ? BUCKETS.filter(b => b.key === bucketFilter) : BUCKETS;
    const bucketLabel = bucketFilter ? (BUCKETS.find(b => b.key === bucketFilter) || {}).label : 'All buckets';
    const title = `Cash Book Statement ${from} to ${to}`;
    const bucketCols = activeBuckets.map(b => `<th style="text-align:right;background:#eef2ff;padding:8px;border:1px solid #e5e7eb">${esc(b.label)}</th>`).join('');
    const openingCells = activeBuckets.map(b => `<td style="text-align:right;padding:6px 8px;border:1px solid #e5e7eb;color:#6b7280;font-style:italic">${b.currency}${fmt(data.opening[b.key])}</td>`).join('');
    const closingCells = activeBuckets.map(b => `<td style="text-align:right;padding:6px 8px;border:1px solid #e5e7eb;font-weight:700;color:#4f46e5">${b.currency}${fmt(data.closing[b.key])}</td>`).join('');
    // v1.9.91 — colour-code rows by type so CR (money in), PV (money out)
    // and TRF (transfer) are instantly distinguishable in the printed
    // statement.
    //   CR  → light green row + green ref pill
    //   PV  → light red   row + red   ref pill
    //   TRF → light blue  row + blue  ref pill
    const refKind = (ref) => {
      const s = String(ref || '').toUpperCase();
      if (s.startsWith('CR'))  return 'cr';
      if (s.startsWith('PV'))  return 'pv';
      if (s.startsWith('TRF')) return 'trf';
      if (s.startsWith('EXC')) return 'exc';
      return 'other';
    };
    const rowStyle = {
      cr:    { bg: '#f0fdf4', pillBg: '#dcfce7', pillColor: '#166534' },
      pv:    { bg: '#fef2f2', pillBg: '#fee2e2', pillColor: '#991b1b' },
      trf:   { bg: '#eff6ff', pillBg: '#dbeafe', pillColor: '#1e40af' },
      exc:   { bg: '#fefce8', pillBg: '#fef08a', pillColor: '#854d0e' },
      other: { bg: '#ffffff', pillBg: '#f3f4f6', pillColor: '#374151' },
    };
    const entryRows = rows.map((e, i) => {
      const sign = e.type === 'credit' ? '+' : '-';
      const color = e.type === 'credit' ? '#16a34a' : '#dc2626';
      const kind = refKind(e.ref);
      const rs = rowStyle[kind];
      const cells = activeBuckets.map(b => {
        const v = parseFloat(e[b.key] || 0);
        if (!v) return `<td style="text-align:right;padding:6px 8px;border:1px solid #e5e7eb;color:#9ca3af;background:${rs.bg}">—</td>`;
        return `<td style="text-align:right;padding:6px 8px;border:1px solid #e5e7eb;color:${color};font-weight:600;background:${rs.bg}">${sign}${b.currency}${fmt(v)}</td>`;
      }).join('');
      return `<tr>
        <td style="padding:6px 8px;border:1px solid #e5e7eb;background:${rs.bg}">${i + 1}</td>
        <td style="padding:6px 8px;border:1px solid #e5e7eb;white-space:nowrap;background:${rs.bg}">${esc(e.date || '')}</td>
        <td style="padding:6px 8px;border:1px solid #e5e7eb;white-space:nowrap;background:${rs.bg}"><span style="display:inline-block;padding:2px 8px;border-radius:12px;background:${rs.pillBg};color:${rs.pillColor};font-weight:700;font-size:11px">${esc(e.ref || '')}</span></td>
        <td style="padding:6px 8px;border:1px solid #e5e7eb;background:${rs.bg}">${esc(e.description || '')}</td>
        ${cells}
      </tr>`;
    }).join('');
    // Legend printed under the title so readers know what the colours mean.
    const legend = `<div style="display:flex;gap:14px;font-size:10.5px;margin-top:4px;flex-wrap:wrap">
      <span><span style="display:inline-block;width:10px;height:10px;background:#dcfce7;border:1px solid #86efac;border-radius:2px;vertical-align:middle;margin-right:4px"></span>CR — Cash Receipt (money in)</span>
      <span><span style="display:inline-block;width:10px;height:10px;background:#fee2e2;border:1px solid #fca5a5;border-radius:2px;vertical-align:middle;margin-right:4px"></span>PV — Payment Voucher (money out)</span>
      <span><span style="display:inline-block;width:10px;height:10px;background:#dbeafe;border:1px solid #93c5fd;border-radius:2px;vertical-align:middle;margin-right:4px"></span>TRF — Transfer</span>
      <span><span style="display:inline-block;width:10px;height:10px;background:#fef08a;border:1px solid #facc15;border-radius:2px;vertical-align:middle;margin-right:4px"></span>EXC — Currency Exchange</span>
    </div>`;
    return `<!doctype html>
<html>
<head>
  <meta charset="utf-8">
  <title>${esc(title)}</title>
  <style>
    @page { size: A4 portrait; margin: 12mm; }
    * { box-sizing: border-box; }
    body { font-family: 'Inter', -apple-system, 'Segoe UI', 'Noto Sans Ethiopic', sans-serif; margin: 0; color: #1f2937; font-size: 12px; line-height: 1.4; }
    h1 { font-size: 20px; color: #4f46e5; margin: 0 0 2px; }
    .sub { color: #6b7280; font-size: 11px; margin-bottom: 14px; }
    .meta { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 14px; padding-bottom: 10px; border-bottom: 2px solid #4f46e5; }
    .meta .right { text-align: right; color: #374151; font-size: 11px; }
    table { width: 100%; border-collapse: collapse; margin-top: 6px; }
    thead th { background: #4f46e5; color: #fff; padding: 8px; border: 1px solid #4f46e5; text-align: left; font-size: 11px; }
    thead th.num { text-align: right; }
    tfoot td { background: #f9fafb; font-weight: 700; }
    .anchor-row td { background: #f3f4f6; font-weight: 700; }
    .footer { margin-top: 18px; font-size: 10px; color: #9ca3af; text-align: center; border-top: 1px solid #e5e7eb; padding-top: 8px; }
  </style>
</head>
<body>
  <div class="meta">
    <div>
      <h1>Cash Book Statement</h1>
      <div class="sub">St. Michael Orthodox Tewahedo Church · Zambia</div>
      ${legend}
    </div>
    <div class="right">
      <div><strong>Period:</strong> ${esc(from)} → ${esc(to)}</div>
      <div><strong>Bucket:</strong> ${esc(bucketLabel)}</div>
      <div><strong>Printed:</strong> ${esc(new Date().toLocaleString())}</div>
    </div>
  </div>
  <table>
    <thead>
      <tr>
        <th style="width:30px">#</th>
        <th style="width:85px">Date</th>
        <th style="width:95px">Ref</th>
        <th>Description</th>
        ${bucketCols}
      </tr>
    </thead>
    <tbody>
      <tr class="anchor-row">
        <td colSpan="4" style="padding:6px 8px;border:1px solid #e5e7eb">Opening Balance${data.openingDate ? ` (${esc(data.openingDate)})` : ''}</td>
        ${openingCells.replace(/font-style:italic/g, '')}
      </tr>
      ${entryRows || `<tr><td colspan="${4 + activeBuckets.length}" style="padding:14px;text-align:center;color:#9ca3af;border:1px solid #e5e7eb">No entries in this period.</td></tr>`}
      <tr class="anchor-row">
        <td colSpan="4" style="padding:6px 8px;border:1px solid #e5e7eb">Closing Balance</td>
        ${closingCells}
      </tr>
    </tbody>
  </table>
  <div class="footer">Generated by Church-POS · kidusmichaelzm.com</div>
</body>
</html>`;
  };

  return (
    <div className="page-content">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
        <div><h1>{t('nav.cashbook')}</h1></div>
        <div style={{ display: 'inline-flex', gap: 8 }}>
          {!isMobile && (
            <button
              className="btn btn-secondary"
              onClick={() => printHtml(buildStatementHtml())}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <FiPrinter /> Print
            </button>
          )}
          <button
            className="btn btn-primary"
            onClick={() => { setTrfError(''); setShowTransfer(true); }}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <FiRepeat /> Transfer
          </button>
          <button
            className="btn btn-primary"
            onClick={() => { setExcError(''); setShowExchange(true); }}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: '#16a34a' }}
            title="Convert between USD and Kwacha buckets at an agreed rate"
          >
            <FiDollarSign /> Exchange
          </button>
        </div>
      </div>

      {/* v1.9.66 — Compact one-line filter bar. Bypasses .filters-row
          class because the mobile CSS forces it to flex-column.
          v1.9.69 — added Bucket dropdown to filter the ledger by
          USD Cash / USD Bank / K Cash / K Bank / K Mobile. */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: 8,
          alignItems: 'end',
          marginBottom: 16,
        }}
      >
        <label style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
          <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>{t('table.from')}</span>
          <input
            type="date"
            value={from}
            onChange={e => setFrom(e.target.value)}
            style={{ width: '100%', padding: '7px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
          <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>{t('table.to')}</span>
          <input
            type="date"
            value={to}
            onChange={e => setTo(e.target.value)}
            style={{ width: '100%', padding: '7px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>
          <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>Bucket</span>
          <select
            value={bucketFilter}
            onChange={e => setBucketFilter(e.target.value)}
            style={{ width: '100%', padding: '7px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, background: '#fff', boxSizing: 'border-box' }}
          >
            <option value="">All buckets</option>
            {BUCKETS.map(b => <option key={b.key} value={b.key}>{b.label}</option>)}
          </select>
        </label>
        {(from || to || bucketFilter) && (
          <button
            onClick={() => { setFrom(''); setTo(''); setBucketFilter(''); }}
            style={{ alignSelf: 'end', padding: '7px 12px', border: '1px solid #d1d5db', borderRadius: 6, background: '#fff', color: '#374151', fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
            title="Clear all filters"
          >Clear</button>
        )}
      </div>

      {/* ── Opening / Closing summary cards ─────────────────────────────
          Two-up on wide screens; stacks to one column below 720px so the
          Closing card no longer clips off the right edge on phones. */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: 12,
          marginBottom: 16,
        }}
      >
        <SummaryCard
          title={t('cashbook.opening', { defaultValue: 'Opening Balance' }) + (bucketFilter ? ` — ${(BUCKETS.find(b => b.key === bucketFilter) || {}).label || ''}` : '')}
          subtitle={data.openingDate ? `${t('cashbook.asOf', { defaultValue: 'as of' })} ${data.openingDate}` : t('cashbook.notSet', { defaultValue: 'Not set — go to Settings → Opening Balance' })}
          buckets={scoped(data.opening)}
          accent="#6b7280"
        />
        <SummaryCard
          title={t('cashbook.closing', { defaultValue: 'Closing Balance' }) + (bucketFilter ? ` — ${(BUCKETS.find(b => b.key === bucketFilter) || {}).label || ''}` : '')}
          subtitle={`${from} → ${to}`}
          buckets={scoped(data.closing)}
          accent="#2563eb"
        />
      </div>

      {/* ── Period totals strip ───────────────────────────────────────
          v1.9.67 — wrap instead of forcing 3 columns, so at zoom /
          narrow widths the Expense/Balance cards don't get clipped. */}
      <div className="stat-cards" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginBottom: 20 }}>
        {/* v1.9.107 — these three tiles scope to the selected bucket (via scoped()).
            When "All buckets" is chosen, show both $ and K totals. When a specific
            bucket is picked, show just that bucket's figure in its own currency. */}
        {(() => {
          const inc = scoped(data.creditTotals);
          const exp = scoped(data.debitTotals);
          const bal = scoped(data.closing);
          const render = (b) => bucketFilter
            ? `${(BUCKETS.find(x => x.key === bucketFilter) || {}).currency || ''}${fmt(b[bucketFilter] || 0)}`
            : `$${fmt(sumGroup(b, 'usd'))} · K${fmt(sumGroup(b, 'k'))}`;
          return (
            <>
              <div className="stat-card">
                <div className="stat-icon" style={{ background: '#dcfce7', color: '#16a34a' }}><FiBook /></div>
                <div>
                  <div className="stat-label">{t('dashboard.income')}{bucketFilter ? ` — ${(BUCKETS.find(b => b.key === bucketFilter) || {}).label || ''}` : ''}</div>
                  <div className="stat-value" style={{ color: '#16a34a', fontSize: 16 }}>{render(inc)}</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon" style={{ background: '#fee2e2', color: '#dc2626' }}><FiBook /></div>
                <div>
                  <div className="stat-label">{t('dashboard.expense')}{bucketFilter ? ` — ${(BUCKETS.find(b => b.key === bucketFilter) || {}).label || ''}` : ''}</div>
                  <div className="stat-value" style={{ color: '#dc2626', fontSize: 16 }}>{render(exp)}</div>
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-icon" style={{ background: '#dbeafe', color: '#2563eb' }}><FiBook /></div>
                <div>
                  <div className="stat-label">{t('dashboard.balance')}{bucketFilter ? ` — ${(BUCKETS.find(b => b.key === bucketFilter) || {}).label || ''}` : ''}</div>
                  <div className="stat-value" style={{ color: '#2563eb', fontSize: 16 }}>{render(bal)}</div>
                </div>
              </div>
            </>
          );
        })()}
      </div>

      {/* v1.9.67 — Tab bar to switch between the full ledger and a
          transfers-only history view. */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 12, borderBottom: '2px solid #e5e7eb' }}>
        {[
          { key: 'ledger',    label: 'Cash Book' },
          { key: 'transfers', label: 'Transfer History' },
          { key: 'exchanges', label: 'Exchange History' },
        ].map(tt => (
          <button
            key={tt.key}
            onClick={() => setTab(tt.key)}
            style={{
              padding: '10px 16px',
              background: 'transparent',
              border: 'none',
              borderBottom: tab === tt.key ? '3px solid #4f46e5' : '3px solid transparent',
              marginBottom: '-2px',
              color: tab === tt.key ? '#4f46e5' : '#6b7280',
              fontWeight: tab === tt.key ? 700 : 500,
              fontSize: 14,
              cursor: 'pointer',
            }}
          >{tt.label}</button>
        ))}
      </div>

      {/* ── Per-bucket ledger ─────────────────────────────────────────
          v1.9.70 — Fixed max-height container so the sticky <th>s
          actually stick as you scroll through long ledgers. Without an
          internal scroll region the sticky headers only stuck to the
          container top (which itself scrolled off with the page). */}
      {tab === 'ledger' && (
      <div className="table-card" style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '70vh' }}>
        <table style={{ minWidth: 1100 }}>
          <thead>
            <tr>
              <th rowSpan={2} style={{ position: 'sticky', top: 0, background: '#f9fafb', zIndex: 3 }}>#</th>
              <th rowSpan={2} style={{ position: 'sticky', top: 0, background: '#f9fafb', zIndex: 3 }}>{t('common.date')}</th>
              <th rowSpan={2} style={{ position: 'sticky', top: 0, background: '#f9fafb', zIndex: 3 }}>{t('finance.crNumber')}</th>
              <th rowSpan={2} style={{ position: 'sticky', top: 0, background: '#f9fafb', zIndex: 3 }}>{t('table.description')}</th>
              <th colSpan={2} style={{ background: '#f0fdf4', color: '#16a34a', position: 'sticky', top: 0, zIndex: 3 }}>USD</th>
              <th colSpan={3} style={{ background: '#eff6ff', color: '#2563eb', position: 'sticky', top: 0, zIndex: 3 }}>Kwacha (K)</th>
            </tr>
            <tr>
              <th style={{ background: '#f0fdf4', position: 'sticky', top: 34, zIndex: 3 }}>Cash</th>
              <th style={{ background: '#f0fdf4', position: 'sticky', top: 34, zIndex: 3 }}>Bank</th>
              <th style={{ background: '#eff6ff', position: 'sticky', top: 34, zIndex: 3 }}>Cash</th>
              <th style={{ background: '#eff6ff', position: 'sticky', top: 34, zIndex: 3 }}>Bank</th>
              <th style={{ background: '#eff6ff', position: 'sticky', top: 34, zIndex: 3 }}>Mobile</th>
            </tr>
          </thead>
          <tbody>
            {/* Opening row — data-label on each bucket cell so mobile card
                view (thead hidden by responsive CSS) still shows what each
                number represents. */}
            <tr style={{ background: '#fafafa', fontStyle: 'italic' }} className="cb-summary-row">
              <td colSpan={4} data-label={t('cashbook.opening', { defaultValue: 'Opening Balance' })} style={{ color: '#6b7280', fontWeight: 600 }}>
                {t('cashbook.opening', { defaultValue: 'Opening Balance' })}
                {data.openingDate ? ` (${data.openingDate})` : ''}
              </td>
              {BUCKETS.map(b => (
                <td key={b.key} data-label={b.label} style={{ color: '#6b7280', fontWeight: 600 }}>
                  {b.currency}{fmt(data.opening[b.key])}
                </td>
              ))}
            </tr>

            {/* Entries — v1.9.69 bucket filter + v1.9.86 ASC sort
                (oldest first, bank-statement style). Opening row is anchored
                at the top, Closing at the bottom, so the ledger reads
                Opening -> every transaction in chronological order -> Closing. */}
            {(() => {
              const filtered = bucketFilter
                ? data.entries.filter(e => parseFloat(e[bucketFilter] || 0) !== 0)
                : data.entries;
              const visibleEntries = filtered.slice().sort((a, b) =>
                (a.date || '').localeCompare(b.date || '') || String(a.ref || '').localeCompare(String(b.ref || ''))
              );
              if (visibleEntries.length === 0) {
                return (
                  <tr><td colSpan={9}><div className="empty-state"><p>
                    {bucketFilter
                      ? `No entries touched ${(BUCKETS.find(b => b.key === bucketFilter) || {}).label || bucketFilter} in this period.`
                      : t('finance.noRecords')}
                  </p></div></td></tr>
                );
              }
              return visibleEntries.map((e, i) => {
              // v1.9.27 — deleted CRs are rendered with strikethrough dashes
              // and don't touch the running balance. Live rows do the usual math.
              if (!e.is_deleted) {
                const sign = e.type === 'credit' ? 1 : -1;
                BUCKETS.forEach(b => { running[b.key] += sign * parseFloat(e[b.key] || 0); });
              }
              // v1.9.61 — Transfer row (bucket → bucket). Distinct purple
              // background, shows both sides with signed values.
              if (e.type === 'transfer') {
                return (
                  <tr key={`trf-${e.ref}-${i}`} style={{ background: '#f5f3ff' }}>
                    <td data-label="#">{i + 1}</td>
                    <td data-label={t('common.date')}>{e.date}</td>
                    <td data-label={t('finance.crNumber')}>
                      <span className="pill" style={{ background: '#ede9fe', color: '#6d28d9', fontFamily: 'Courier New, monospace', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <FiRepeat size={11} /> {e.ref}
                      </span>
                      {e.attachment && (
                        <button
                          onClick={() => setViewAttachment(e.attachment)}
                          title="View deposit slip / receipt"
                          style={{ marginLeft: 6, background: 'transparent', border: 'none', color: '#7c3aed', cursor: 'pointer', padding: 2, verticalAlign: 'middle' }}
                        ><FiPaperclip size={14} /></button>
                      )}
                    </td>
                    <td data-label={t('table.description')} style={{ maxWidth: 240 }}>
                      <div style={{ fontWeight: 600, color: '#6d28d9' }}>
                        {bucketLabel(e.from_bucket)} → {bucketLabel(e.to_bucket)}
                      </div>
                      {e.description && e.description !== `Transfer: ${e.from_bucket} → ${e.to_bucket}` && (
                        <div style={{ fontSize: 11, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.description}</div>
                      )}
                    </td>
                    {BUCKETS.map(b => {
                      const v = parseFloat(e[b.key] || 0);
                      if (v === 0) return <td key={b.key} data-label={b.label} style={{ color: '#d1d5db' }}>—</td>;
                      return (
                        <td key={b.key} data-label={b.label} style={{ color: v > 0 ? '#7c3aed' : '#a855f7', fontWeight: 600 }}>
                          {v > 0 ? '+' : '−'}{b.currency}{fmt(Math.abs(v))}
                          <button
                            onClick={() => removeTransfer(e.transfer_id, e.ref)}
                            title="Delete this transfer"
                            style={{ marginLeft: 8, background: 'transparent', border: 'none', color: '#a855f7', cursor: 'pointer', padding: 0, display: v > 0 ? 'inline' : 'none' }}
                          ><FiTrash2 size={12} /></button>
                        </td>
                      );
                    })}
                  </tr>
                );
              }
              if (e.is_deleted) {
                return (
                  <React.Fragment key={`${e.ref}-${i}`}>
                    <tr style={{ background: '#fef2f2' }}>
                      <td data-label="#" style={{ color: '#9ca3af' }}>{i + 1}</td>
                      <td data-label={t('common.date')} style={{ color: '#9ca3af', textDecoration: 'line-through' }}>{e.date}</td>
                      <td data-label={t('finance.crNumber')}>
                        <span className="pill" style={{ background: 'transparent', border: '1px dashed #fecaca', color: '#9ca3af', fontFamily: 'Courier New, monospace' }}>{e.ref}</span>
                      </td>
                      <td data-label={t('table.description')} style={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#9ca3af', textDecoration: 'line-through' }}>
                        {e.description}
                      </td>
                      {BUCKETS.map(b => (
                        <td key={b.key} data-label={b.label} style={{ color: '#9ca3af', fontFamily: 'Courier New, monospace', letterSpacing: 1, textDecoration: 'line-through' }}>———</td>
                      ))}
                    </tr>
                    <tr style={{ background: '#fef1f1' }}>
                      <td colSpan={9} style={{ padding: '8px 12px 12px', fontSize: 11.5, color: '#991b1b', borderTop: 0 }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px 20px', alignItems: 'center' }}>
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 6px',
                            background: 'rgba(220,38,38,0.08)', border: '1px solid rgba(220,38,38,0.25)',
                            borderRadius: 4, fontWeight: 700, fontSize: 10.5, letterSpacing: '0.1em', textTransform: 'uppercase'
                          }}>✕ Deleted</span>
                          {e.del_reason && (
                            <span><span style={{ opacity: 0.75, fontWeight: 600 }}>Reason:</span> <i style={{ fontWeight: 700 }}>"{e.del_reason}"</i></span>
                          )}
                          {e.del_requested_by_name && (
                            <span>
                              <span style={{ opacity: 0.75, fontWeight: 600 }}>Requested by:</span> <b>{e.del_requested_by_name}</b>
                              {e.del_requester_role && <span style={{ fontFamily: 'Courier New, monospace', opacity: 0.7 }}> ({e.del_requester_role})</span>}
                            </span>
                          )}
                          {e.del_reviewed_by_name && (
                            <span>
                              <span style={{ opacity: 0.75, fontWeight: 600 }}>Approved by:</span> <b>{e.del_reviewed_by_name}</b>
                              {e.del_reviewer_role && <span style={{ fontFamily: 'Courier New, monospace', opacity: 0.7 }}> ({e.del_reviewer_role})</span>}
                            </span>
                          )}
                          {e.del_reviewed_at && (
                            <span><span style={{ opacity: 0.75, fontWeight: 600 }}>When:</span> <span style={{ fontFamily: 'Courier New, monospace', fontWeight: 700 }}>{String(e.del_reviewed_at).slice(0, 16).replace('T', ' ')}</span></span>
                          )}
                        </div>
                      </td>
                    </tr>
                  </React.Fragment>
                );
              }
              return (
                <tr key={`${e.ref}-${i}`}>
                  <td data-label="#">{i + 1}</td>
                  <td data-label={t('common.date')}>{e.date}</td>
                  <td data-label={t('finance.crNumber')}>
                    <span className={`pill ${e.type === 'credit' ? 'pill-green' : 'pill-red'}`}>{e.ref}</span>
                  </td>
                  <td data-label={t('table.description')} style={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {e.description}
                  </td>
                  {BUCKETS.map(b => {
                    const v = parseFloat(e[b.key] || 0);
                    if (v === 0) return <td key={b.key} data-label={b.label} style={{ color: '#d1d5db' }}>—</td>;
                    return (
                      <td key={b.key} data-label={b.label} style={{ color: e.type === 'credit' ? '#16a34a' : '#dc2626', fontWeight: 600 }}>
                        {e.type === 'credit' ? '+' : '−'}{b.currency}{fmt(v)}
                      </td>
                    );
                  })}
                </tr>
              );
            });
            })()}

            {/* Closing row — ALWAYS rendered so it's visible even when the
                date filter has zero entries (closing = opening in that case,
                still meaningful info). Same data-label pattern as Opening. */}
            <tr style={{ background: '#eff6ff', fontWeight: 700 }} className="cb-summary-row">
              <td colSpan={4} data-label={t('cashbook.closing', { defaultValue: 'Closing Balance' })} style={{ color: '#1d4ed8' }}>
                {t('cashbook.closing', { defaultValue: 'Closing Balance' })}
              </td>
              {BUCKETS.map(b => (
                <td key={b.key} data-label={b.label} style={{ color: data.closing[b.key] >= 0 ? '#1d4ed8' : '#dc2626' }}>
                  {b.currency}{fmt(data.closing[b.key])}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
      )}

      {/* v1.9.67 — Transfer History tab: dedicated view of just the
          bucket-to-bucket moves with full metadata (created by / attachment / delete). */}
      {tab === 'transfers' && (
        <div className="table-card" style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '70vh' }}>
          <table style={{ minWidth: 720 }}>
            <thead>
              <tr>
                <th style={{ position: 'sticky', top: 0, background: '#f9fafb', zIndex: 3 }}>#</th>
                <th style={{ position: 'sticky', top: 0, background: '#f9fafb', zIndex: 3 }}>Date</th>
                <th style={{ position: 'sticky', top: 0, background: '#f9fafb', zIndex: 3 }}>Ref</th>
                <th style={{ position: 'sticky', top: 0, background: '#f9fafb', zIndex: 3 }}>From</th>
                <th style={{ position: 'sticky', top: 0, background: '#f9fafb', zIndex: 3 }}>To</th>
                <th style={{ position: 'sticky', top: 0, background: '#f9fafb', zIndex: 3, textAlign: 'right' }}>Amount</th>
                <th style={{ position: 'sticky', top: 0, background: '#f9fafb', zIndex: 3 }}>Description</th>
                <th style={{ position: 'sticky', top: 0, background: '#f9fafb', zIndex: 3 }}>By</th>
                <th style={{ position: 'sticky', top: 0, background: '#f9fafb', zIndex: 3, textAlign: 'center' }}>Slip</th>
                <th style={{ position: 'sticky', top: 0, background: '#f9fafb', zIndex: 3 }}></th>
              </tr>
            </thead>
            <tbody>
              {transfers.length === 0 ? (
                <tr><td colSpan={10}><div className="empty-state"><p>No transfers in this period.</p></div></td></tr>
              ) : transfers.map((t2, i) => {
                const cur = (BUCKETS.find(b => b.key === t2.from_bucket) || {}).currency || '';
                return (
                  <tr key={t2.id} style={{ background: i % 2 ? '#faf5ff' : '#fff' }}>
                    <td data-label="#">{i + 1}</td>
                    <td data-label="Date">{t2.date}</td>
                    <td data-label="Ref">
                      <span className="pill" style={{ background: '#ede9fe', color: '#6d28d9', fontFamily: 'Courier New, monospace' }}>{t2.transfer_number}</span>
                    </td>
                    <td data-label="From" style={{ fontWeight: 600 }}>{bucketLabel(t2.from_bucket)}</td>
                    <td data-label="To" style={{ fontWeight: 600 }}>{bucketLabel(t2.to_bucket)}</td>
                    <td data-label="Amount" style={{ textAlign: 'right', fontWeight: 700, color: '#7c3aed', fontFamily: 'ui-monospace, monospace' }}>
                      {cur}{fmt(t2.amount)}
                    </td>
                    <td data-label="Description" style={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#6b7280' }}>
                      {t2.description || '—'}
                    </td>
                    <td data-label="By" style={{ fontSize: 12, color: '#6b7280' }}>{t2.created_by_name || '—'}</td>
                    <td data-label="Slip" style={{ textAlign: 'center' }}>
                      {t2.attachment ? (
                        <button
                          onClick={() => setViewAttachment(t2.attachment)}
                          title="View attachment"
                          style={{ background: 'transparent', border: 'none', color: '#7c3aed', cursor: 'pointer', padding: 4 }}
                        ><FiPaperclip size={16} /></button>
                      ) : <span style={{ color: '#d1d5db' }}>—</span>}
                    </td>
                    <td>
                      <button
                        className="btn-icon danger"
                        onClick={() => removeTransfer(t2.id, t2.transfer_number)}
                        title="Delete this transfer"
                      ><FiTrash2 /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {transfers.length > 0 && (
              <tfoot>
                <tr style={{ background: '#f5f3ff', fontWeight: 700 }}>
                  <td colSpan={5}>Total ({transfers.length} transfer{transfers.length === 1 ? '' : 's'})</td>
                  <td colSpan={5} style={{ fontSize: 12, color: '#6b7280', fontStyle: 'italic' }}>
                    Transfers move money between buckets — they do not change the total balance.
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      {/* v1.9.119 — Exchange History tab: USD <-> K conversions with the rate
          used, who recorded them, the slip and delete — mirrors Transfer History. */}
      {tab === 'exchanges' && (
        <div className="table-card" style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '70vh' }}>
          <table style={{ minWidth: 860 }}>
            <thead>
              <tr>
                <th style={{ position: 'sticky', top: 0, background: '#f9fafb', zIndex: 3 }}>#</th>
                <th style={{ position: 'sticky', top: 0, background: '#f9fafb', zIndex: 3 }}>Date</th>
                <th style={{ position: 'sticky', top: 0, background: '#f9fafb', zIndex: 3 }}>Ref</th>
                <th style={{ position: 'sticky', top: 0, background: '#f9fafb', zIndex: 3 }}>From</th>
                <th style={{ position: 'sticky', top: 0, background: '#f9fafb', zIndex: 3 }}>To</th>
                <th style={{ position: 'sticky', top: 0, background: '#f9fafb', zIndex: 3, textAlign: 'right' }}>Amount</th>
                <th style={{ position: 'sticky', top: 0, background: '#f9fafb', zIndex: 3, textAlign: 'right' }}>Rate (K/$)</th>
                <th style={{ position: 'sticky', top: 0, background: '#f9fafb', zIndex: 3, textAlign: 'right' }}>Received</th>
                <th style={{ position: 'sticky', top: 0, background: '#f9fafb', zIndex: 3 }}>Description</th>
                <th style={{ position: 'sticky', top: 0, background: '#f9fafb', zIndex: 3 }}>By</th>
                <th style={{ position: 'sticky', top: 0, background: '#f9fafb', zIndex: 3, textAlign: 'center' }}>Slip</th>
                <th style={{ position: 'sticky', top: 0, background: '#f9fafb', zIndex: 3 }}></th>
              </tr>
            </thead>
            <tbody>
              {exchanges.length === 0 ? (
                <tr><td colSpan={12}><div className="empty-state"><p>No exchanges in this period.</p></div></td></tr>
              ) : exchanges.map((e2, i) => {
                const fromCur = (BUCKETS.find(b => b.key === e2.from_bucket) || {}).currency || '';
                const toCur   = (BUCKETS.find(b => b.key === e2.to_bucket) || {}).currency || '';
                return (
                  <tr key={e2.id} style={{ background: i % 2 ? '#f0fdf4' : '#fff' }}>
                    <td data-label="#">{i + 1}</td>
                    <td data-label="Date">{e2.date}</td>
                    <td data-label="Ref">
                      <span className="pill" style={{ background: '#dcfce7', color: '#15803d', fontFamily: 'Courier New, monospace' }}>{e2.exchange_number}</span>
                    </td>
                    <td data-label="From" style={{ fontWeight: 600 }}>{bucketLabel(e2.from_bucket)}</td>
                    <td data-label="To" style={{ fontWeight: 600 }}>{bucketLabel(e2.to_bucket)}</td>
                    <td data-label="Amount" style={{ textAlign: 'right', fontWeight: 700, color: '#dc2626', fontFamily: 'ui-monospace, monospace' }}>
                      -{fromCur}{fmt(e2.amount_from)}
                    </td>
                    <td data-label="Rate (K/$)" style={{ textAlign: 'right', fontFamily: 'ui-monospace, monospace' }}>{fmt(e2.rate)}</td>
                    <td data-label="Received" style={{ textAlign: 'right', fontWeight: 700, color: '#16a34a', fontFamily: 'ui-monospace, monospace' }}>
                      +{toCur}{fmt(e2.amount_to)}
                    </td>
                    <td data-label="Description" style={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#6b7280' }}>
                      {e2.description || '—'}
                    </td>
                    <td data-label="By" style={{ fontSize: 12, color: '#6b7280' }}>{e2.created_by_name || '—'}</td>
                    <td data-label="Slip" style={{ textAlign: 'center' }}>
                      {e2.attachment ? (
                        <button
                          onClick={() => setViewAttachment(e2.attachment)}
                          title="View attachment"
                          style={{ background: 'transparent', border: 'none', color: '#16a34a', cursor: 'pointer', padding: 4 }}
                        ><FiPaperclip size={16} /></button>
                      ) : <span style={{ color: '#d1d5db' }}>—</span>}
                    </td>
                    <td>
                      <button
                        className="btn-icon danger"
                        onClick={() => removeExchange(e2.id, e2.exchange_number)}
                        title="Delete this exchange"
                      ><FiTrash2 /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {exchanges.length > 0 && (
              <tfoot>
                <tr style={{ background: '#f0fdf4', fontWeight: 700 }}>
                  <td colSpan={5}>Total ({exchanges.length} exchange{exchanges.length === 1 ? '' : 's'})</td>
                  <td colSpan={7} style={{ fontSize: 12, color: '#6b7280', fontStyle: 'italic' }}>
                    Exchanges convert money between USD and Kwacha at the rate shown.
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      {/* v1.9.62 — Attachment viewer (image lightbox / PDF opener) */}
      {viewAttachment && (
        <div
          onClick={() => setViewAttachment(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 110, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, cursor: 'pointer' }}
        >
          <button
            onClick={() => setViewAttachment(null)}
            style={{ position: 'absolute', top: 12, right: 12, background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', width: 40, height: 40, borderRadius: 20, cursor: 'pointer', display: 'grid', placeItems: 'center' }}
            aria-label="Close"
          ><FiX size={22} /></button>
          {viewAttachment.toLowerCase().endsWith('.pdf') ? (
            <div style={{ background: '#fff', padding: 20, borderRadius: 12, textAlign: 'center', color: '#374151' }} onClick={e => e.stopPropagation()}>
              <FiFile size={48} style={{ color: '#6b7280' }} />
              <div style={{ marginTop: 10, fontSize: 14, fontWeight: 600 }}>{viewAttachment}</div>
              <div style={{ marginTop: 8, fontSize: 12, color: '#9ca3af' }}>PDF preview not supported in-app — download from your device.</div>
            </div>
          ) : (
            <AuthImg
              filename={viewAttachment}
              alt="Attachment"
              style={{ maxWidth: '100%', maxHeight: '90vh', objectFit: 'contain', borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,0.6)' }}
              fallback={<div style={{ color: '#fff' }}>Loading…</div>}
            />
          )}
        </div>
      )}

      {/* v1.9.61 — New Transfer modal */}
      {showTransfer && (
        <div
          onClick={() => !trfBusy && setShowTransfer(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 460, boxShadow: '0 20px 60px rgba(0,0,0,0.35)' }}
          >
            <div style={{ padding: '14px 18px', borderBottom: '1px solid #e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <FiRepeat style={{ color: '#7c3aed' }} />
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>New Transfer</h3>
              </div>
              <button onClick={() => setShowTransfer(false)} disabled={trfBusy} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: 20, padding: 4 }} aria-label="Close"><FiX /></button>
            </div>
            <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Date</label>
                <input type="date" value={trfForm.date} onChange={e => setTrfForm(f => ({ ...f, date: e.target.value }))} style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13 }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>From</label>
                  <select value={trfForm.from_bucket} onChange={e => setTrfForm(f => ({ ...f, from_bucket: e.target.value }))} style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, background: '#fff' }}>
                    {BUCKETS.map(b => <option key={b.key} value={b.key}>{b.label}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>To</label>
                  <select value={trfForm.to_bucket} onChange={e => setTrfForm(f => ({ ...f, to_bucket: e.target.value }))} style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, background: '#fff' }}>
                    {BUCKETS.map(b => <option key={b.key} value={b.key}>{b.label}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
                  Amount ({bucketCurrency(trfForm.from_bucket)})
                </label>
                <input type="number" step="0.01" min="0" value={trfForm.amount} onChange={e => setTrfForm(f => ({ ...f, amount: e.target.value }))} placeholder="0.00" style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 16, fontWeight: 600 }} autoFocus />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>Description <span style={{ fontWeight: 400, textTransform: 'none', color: '#9ca3af' }}>(optional)</span></label>
                <input type="text" value={trfForm.description} onChange={e => setTrfForm(f => ({ ...f, description: e.target.value }))} placeholder="e.g. Deposited K1000 into ABC Bank" style={{ width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13 }} />
              </div>

              {/* v1.9.62 — Attachment: deposit slip or bank receipt.
                  capture="environment" opens the rear camera on phones. */}
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
                  Deposit slip / Receipt <span style={{ fontWeight: 400, textTransform: 'none', color: '#9ca3af' }}>(optional)</span>
                </label>
                <AttachmentPicker file={trfFile} preview={trfFilePreview} onPick={setTrfFileFromInput} onClear={clearTrfFile} />
              </div>

              {trfError && (
                <div style={{ padding: '8px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, fontSize: 12, color: '#991b1b' }}>
                  {trfError}
                </div>
              )}
            </div>
            <div style={{ padding: 14, borderTop: '1px solid #e5e7eb', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setShowTransfer(false)} disabled={trfBusy}>Cancel</button>
              <button className="btn btn-primary" onClick={submitTransfer} disabled={trfBusy || !trfForm.amount}>
                {trfBusy ? 'Saving…' : 'Save Transfer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* v1.9.105 — Currency Exchange modal. USD <-> K at an admin-entered
          rate. amount_to is derived live for display and server-side on save.*/}
      {showExchange && (() => {
        const fromIsUsd = excForm.from_bucket.startsWith('usd');
        const fromCur = fromIsUsd ? '$' : 'K';
        const toCur   = fromIsUsd ? 'K' : '$';
        const triple = computeExchangeTriple();
        // v1.9.110 — tapping the locked (auto) field unlocks it. Of the two
        // fields that were editable, the one the user typed in most recently
        // stays editable; the other becomes the new auto field. (The old
        // version returned early for the locked field, so it never unlocked.)
        const makePrimary = (field) => {
          if (excDerived !== field) return;
          const others = ['amount_from', 'amount_to', 'rate'].filter(f => f !== field);
          const nextDerived = others.find(f => f !== excLastEdited.current) || others[0];
          // Keep the number the user was looking at in the newly-editable field.
          const key = field === 'rate' ? 'rate' : field.replace('amount_', '');
          const v = triple[key] || 0;
          setExcForm(p => ({ ...p, [field]: v > 0 ? String(+v.toFixed(2)) : '' }));
          setExcDerived(nextDerived);
          excLastEdited.current = field;
        };
        const isDerived = (field) => excDerived === field;
        const displayValue = (field) => {
          if (!isDerived(field)) return excForm[field];
          const key = field === 'rate' ? 'rate' : field.replace('amount_', '');
          const v = triple[key] || 0;
          return v > 0 ? v.toFixed(2) : '';
        };
        return (
          <div className="modal-overlay" onClick={() => !excBusy && setShowExchange(false)} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '24px 12px', overflowY: 'auto' }}>
            <div onClick={e => e.stopPropagation()} style={{ width: '100%', maxWidth: 760, background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
              <div style={{ padding: '14px 16px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <strong style={{ fontSize: 17, color: '#111827' }}>💱 Currency Exchange</strong>
                <button onClick={() => setShowExchange(false)} disabled={excBusy} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: 20, padding: 4 }} aria-label="Close"><FiX /></button>
              </div>
              <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div className="form-group">
                  <label>Date</label>
                  <input type="date" value={excForm.date} onChange={e => setExcForm(p => ({ ...p, date: e.target.value }))} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>From ({fromCur})</label>
                    <select value={excForm.from_bucket} onChange={e => {
                      const next = e.target.value;
                      setExcForm(p => ({
                        ...p,
                        from_bucket: next,
                        // Flip to-bucket to the OTHER currency automatically
                        to_bucket: next.startsWith('usd') ? 'k_cash' : 'usd_cash',
                      }));
                    }}>
                      {BUCKETS.map(b => <option key={b.key} value={b.key}>{b.label}</option>)}
                    </select>
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>To ({toCur})</label>
                    <select value={excForm.to_bucket} onChange={e => setExcForm(p => ({ ...p, to_bucket: e.target.value }))}>
                      {BUCKETS.filter(b => (fromIsUsd ? b.group === 'k' : b.group === 'usd')).map(b => <option key={b.key} value={b.key}>{b.label}</option>)}
                    </select>
                  </div>
                </div>
                {/* v1.9.106 — three inputs, two editable, one derived + locked.
                    Click any locked field to make it editable (the oldest editable
                    one becomes the new derived). */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
                  {['amount_from', 'amount_to', 'rate'].map(field => {
                    const locked = isDerived(field);
                    const label  = field === 'amount_from' ? `Amount ${fromCur}` : field === 'amount_to' ? `Amount ${toCur}` : 'Rate (K/$)';
                    return (
                      <div className="form-group" style={{ margin: 0 }} key={field}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          {label}
                          {locked && <span style={{ fontSize: 10, color: '#9ca3af', fontWeight: 400 }}>(auto — tap to edit)</span>}
                        </label>
                        <input
                          type="number" step="0.01" min={0}
                          value={displayValue(field)}
                          onChange={e => {
                            if (locked) return;
                            excLastEdited.current = field;
                            setExcForm(p => ({ ...p, [field]: e.target.value }));
                          }}
                          onFocus={() => { if (locked) makePrimary(field); }}
                          placeholder={field === 'rate' ? 'e.g. 26' : '0.00'}
                          style={locked ? { background: '#f3f4f6', color: '#4b5563', cursor: 'pointer' } : undefined}
                        />
                      </div>
                    );
                  })}
                </div>
                <div style={{ padding: '10px 14px', background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, fontSize: 14, color: '#166534' }}>
                  <strong>Preview: {fromCur}{triple.from.toFixed(2)} → {toCur}{triple.to.toFixed(2)} @ {triple.rate.toFixed(2)}</strong>
                  <div style={{ fontSize: 12, color: '#15803d', marginTop: 2 }}>
                    Enter any two fields — the third fills in automatically.
                  </div>
                </div>
                <div className="form-group">
                  <label>Description (optional)</label>
                  <input value={excForm.description} onChange={e => setExcForm(p => ({ ...p, description: e.target.value }))} placeholder="e.g. Changed at bureau, Oct 12" />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label>Deposit slip / Receipt (optional)</label>
                  <AttachmentPicker file={excFile} preview={excFilePreview} onPick={setExcFileFromInput} onClear={clearExcFile} />
                </div>
                {excError && (
                  <div style={{ padding: '8px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, fontSize: 12, color: '#991b1b' }}>{excError}</div>
                )}
              </div>
              <div style={{ padding: 14, borderTop: '1px solid #e5e7eb', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button className="btn btn-secondary" onClick={() => setShowExchange(false)} disabled={excBusy}>Cancel</button>
                <button className="btn btn-primary" onClick={submitExchange} disabled={excBusy || !(triple.from > 0 && triple.rate > 0)}>
                  {excBusy ? 'Saving…' : 'Save Exchange'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

// v1.9.120 — deposit slip / receipt picker shared by the Transfer and
// Currency Exchange modals. "Take photo" opens the rear camera on phones
// (capture="environment"); "Choose file" accepts images or a PDF.
const AttachmentPicker = ({ file, preview, onPick, onClear }) => (
  !file ? (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', background: '#f9fafb', color: '#374151' }}>
        <FiCamera /> Take photo
        <input
          type="file"
          accept="image/*"
          capture="environment"
          onChange={e => onPick(e.target.files?.[0] || null)}
          style={{ display: 'none' }}
        />
      </label>
      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 14px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', background: '#f9fafb', color: '#374151' }}>
        <FiPaperclip /> Choose file
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp,application/pdf"
          onChange={e => onPick(e.target.files?.[0] || null)}
          style={{ display: 'none' }}
        />
      </label>
    </div>
  ) : (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 10, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8 }}>
      {preview ? (
        <img src={preview} alt="Preview" style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 6, border: '1px solid #d1d5db' }} />
      ) : (
        <div style={{ width: 48, height: 48, display: 'grid', placeItems: 'center', background: '#fff', borderRadius: 6, border: '1px solid #d1d5db', color: '#6b7280' }}><FiFile size={24} /></div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: '#166534', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{file.name}</div>
        <div style={{ fontSize: 11, color: '#6b7280' }}>{(file.size / 1024).toFixed(0)} KB</div>
      </div>
      <button
        onClick={onClear}
        style={{ background: 'transparent', border: 'none', color: '#dc2626', cursor: 'pointer', padding: 4 }}
        title="Remove attachment"
      ><FiX size={18} /></button>
    </div>
  )
);

const SummaryCard = ({ title, subtitle, buckets, accent }) => {
  const usdTotal = parseFloat(buckets.usd_cash || 0) + parseFloat(buckets.usd_bank || 0);
  const kTotal   = parseFloat(buckets.k_cash || 0) + parseFloat(buckets.k_bank || 0) + parseFloat(buckets.k_momo || 0);
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 16 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12 }}>
        <strong style={{ fontSize: 14, color: accent }}>{title}</strong>
        <span style={{ fontSize: 11, color: '#9ca3af' }}>{subtitle}</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div style={{ padding: 10, background: '#f0fdf4', borderRadius: 8 }}>
          <div style={{ fontSize: 11, color: '#16a34a', fontWeight: 600 }}>USD ($)</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#16a34a' }}>${fmt(usdTotal)}</div>
          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
            Cash ${fmt(buckets.usd_cash)} · Bank ${fmt(buckets.usd_bank)}
          </div>
        </div>
        <div style={{ padding: 10, background: '#eff6ff', borderRadius: 8 }}>
          <div style={{ fontSize: 11, color: '#2563eb', fontWeight: 600 }}>Kwacha (K)</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#2563eb' }}>K{fmt(kTotal)}</div>
          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
            Cash K{fmt(buckets.k_cash)} · Bank K{fmt(buckets.k_bank)} · Mob K{fmt(buckets.k_momo)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CashBook;
